import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { useContext } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { ColorPicker } from "@/ui/color-picker";
import { cn } from "@/utils";
import { OnboardingThemeContext } from "@/utils/onboarding-theme";
import type { themeDefaultQuery } from "./__generated__/themeDefaultQuery.graphql";
import type { themeOnboardingUpdateMutation } from "./__generated__/themeOnboardingUpdateMutation.graphql";
import type { themeUpdateMutation } from "./__generated__/themeUpdateMutation.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/theme")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const { capture, trackError } = useOnboardingAnalytics("theme", clubSlug);

	const { theme, setTheme } = useContext(OnboardingThemeContext);

	const data = useLazyLoadQuery<themeDefaultQuery>(
		graphql`
    query themeDefaultQuery {
      defaultThemes {
        id
        name
        variant
        styles
        tier
        rawID
      }
    }
  `,
		{},
	);

	const [updateTheme] = useMutation<themeUpdateMutation>(graphql`
	    mutation themeUpdateMutation($club: String!, $payload: UpdateClubThemeInput!) {
				clubThemeUpdate(club: $club, payload: $payload) {
					club {
						id
					}
				}
			}
	`);

	const [updateClub] = useMutation<themeOnboardingUpdateMutation>(graphql`
      mutation themeOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `);

	const basicThemes = data.defaultThemes.filter(
		(d) => d.tier === "BASIC" && d.variant === "LIGHT",
	);

	const form = useForm({
		defaultValues: {
			theme: basicThemes[0]?.name || "Forest",
		},
		onSubmit: ({ value }) => {
			const selectedThemes = basicThemes.filter((b) => b.name === value.theme);

			return new Promise((resolve, reject) => {
				updateTheme({
					variables: {
						club: clubSlug,
						payload: {
							variants: selectedThemes.map((s) => ({
								originalTheme: parseInt(s.rawID, 10),
								variant: s.variant,
								styles: theme.styles,
							})),
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						updateClub({
							variables: {
								club: onboardingClub?.id ?? "",
								payload: {
									configurations: [
										{
											key: "ONBOARDING_LAST_PAGE",
											value: "theme",
										},
									],
								},
							},
							onCompleted: (_clubResponse, clubErrors) => {
								if (clubErrors?.length) {
									toast.error(clubErrors[0].message);
									reject(new Error(clubErrors[0].message));
									return;
								}
								capture("completed", { theme_name: theme.name });
								toast.success("Tema atualizado com sucesso!");
								navigate({ to: "/onboarding/$club/features" });
								resolve(response);
							},
							onError: (error) => {
								trackError("submission", { error_message: error.message });
								toast.error("Erro ao salvar o progresso do onboarding.");
								reject(error);
							},
						});
					},
					onError: (error) => {
						trackError("submission", { error_message: error.message });
						console.error(error);
						reject(error);
					},
				});
			});
		},
	});

	const themeGrid = {
		"1": "grid-cols-1",
		"2": "grid-cols-2",
		"3": "grid-cols-3",
		"4": "grid-cols-4",
		"5": "grid-cols-5",
		"6": "grid-cols-6",
		"7": "grid-cols-7",
		"8": "grid-cols-8",
		"9": "grid-cols-9",
		"10": "grid-cols-10",
	}[basicThemes.length.toString()];

	return (
		<form
			className="container mx-auto p-6 max-w-2xl flex flex-col"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<Card className="p-6 gap-0">
				<div>
					<Heading>Escolha o visual da sua comunidade</Heading>

					<Text>Você poderá mudar e personalizar isso depois:</Text>
				</div>

				<div className={cn("gap-3 mt-6 grid", themeGrid)}>
					{basicThemes.map((t) => (
						<button
							type="button"
							key={t.name}
							className={cn(
								"border rounded-md p-3 flex flex-col items-start justify-start",
								t.name.toLowerCase().replaceAll(" ", "-") ===
									theme.name.toLowerCase()
									? "border-primary! bg-accent"
									: "",
							)}
							style={{
								fontFamily: t.styles["font-sans"],
							}}
							onClick={(e) => {
								const x = (e.clientX / window.innerWidth) * 100;
								const y = (e.clientY / window.innerHeight) * 100;

								document.documentElement.style.setProperty("--x", `${x}%`);
								document.documentElement.style.setProperty("--y", `${y}%`);

								const themeName = t.name.toLowerCase().replaceAll(" ", "-") as
									| "gold"
									| "forest"
									| "quiet-luxury";
								if ("startViewTransition" in document) {
									document.startViewTransition(() => {
										setTheme({ name: themeName, styles: t.styles });
									});
								} else {
									setTheme({ name: themeName, styles: t.styles });
								}
							}}
						>
							<div className="text-left">{t.name}</div>

							<div className="flex flex-row items-center justify-start mt-3">
								{[
									t.styles.primary,
									t.styles.accent,
									t.styles.secondary,
									t.styles.border,
								].map((d, index) => (
									<div
										className="size-4 rounded-full border border-gray-200"
										key={index}
										style={{
											backgroundColor: d,
											translate: index * -4,
										}}
									/>
								))}
							</div>
						</button>
					))}
				</div>

				<Collapsible>
					<CollapsibleTrigger
						className={cn(
							"flex group mt-6 flex-row items-center justify-start gap-2 font-medium select-none text-base/6 text-foreground data-disabled:opacity-50 sm:text-sm/6 dark:text-white",
							"data-[state=open]:mb-3 data-[state=closed]:mb-3",
						)}
					>
						Configurações Avançadas
						<Icon
							name="ChevronDown"
							className="group-data-[state=open]:rotate-180 size-4 text-muted-foreground"
						/>
					</CollapsibleTrigger>

					<CollapsibleContent className="flex flex-col gap-6">
						<ColorPicker
							value={theme.styles.primary}
							label="Primary"
							className=""
							onChange={(val) =>
								setTheme({
									...theme,
									styles: {
										...theme.styles,
										primary: val,
										ring: val,
										"sidebar-primary": val,
										"sidebar-ring": val,
									},
								})
							}
						/>
						<ColorPicker
							value={theme.styles["primary-foreground"]}
							label="Primary - Texto"
							className="mb-3"
							onChange={(val) =>
								setTheme({
									...theme,
									styles: {
										...theme.styles,
										"primary-foreground": val,
										"sidebar-primary-foreground": val,
									},
								})
							}
						/>

						<ColorPicker
							value={theme.styles.accent}
							label="Cor de Destaque"
							className=""
							onChange={(val) =>
								setTheme({
									...theme,
									styles: {
										...theme.styles,
										accent: val,
										"sidebar-accent": val,
									},
								})
							}
						/>
						<ColorPicker
							value={theme.styles["accent-foreground"]}
							label="Cor de Destaque - Texto"
							className="mb-3"
							onChange={(val) =>
								setTheme({
									...theme,
									styles: {
										...theme.styles,
										"accent-foreground": val,
										"sidebar-accent-foreground": val,
									},
								})
							}
						/>
					</CollapsibleContent>
				</Collapsible>

				<Separator className="my-3" />

				<form.Subscribe selector={(state) => [state.isSubmitting]}>
					{([isSubmitting]) => (
						<div className="pt-3 flex items-center justify-end">
							<Button
								type="submit"
								disabled={isSubmitting}
								className="flex items-center gap-2"
							>
								Continuar
								{isSubmitting && <Spinner />}
							</Button>
						</div>
					)}
				</form.Subscribe>
			</Card>
		</form>
	);
}
